"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createInstanceUtils = exports.resolveLinearChainState = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const dbNodes = __importStar(require("../dbNodes/dbNodes"));
const makeSyntheticId = (id) => `fake-${id}`;
const isSyntheticId = (id) => id.startsWith('fake-');
const dbInstanceToPipelineInstance = (instance) => {
    const { id, artifactId, updated_at, created_at } = instance;
    const base = {
        id,
        artifactId,
        updated_at,
        created_at,
        synthetic: isSyntheticId(id),
    };
    // Narrow on status so statusDetails stays paired with its own variant.
    switch (instance.status) {
        case 'initializing':
            return {
                ...base,
                status: instance.status,
                statusDetails: instance.statusDetails,
            };
        case 'inProgress':
            return {
                ...base,
                status: instance.status,
                statusDetails: instance.statusDetails,
            };
        case 'error':
            return {
                ...base,
                status: instance.status,
                statusDetails: instance.statusDetails,
            };
        case 'success':
            return {
                ...base,
                status: instance.status,
                statusDetails: instance.statusDetails,
            };
        default:
            return (0, common_1.finleyAssertUnreachable)(instance);
    }
};
const resolveLinearChainState = (chain) => {
    const failed = chain.find(({ instance }) => instance?.status === 'error');
    if (failed?.instance) {
        return { status: 'error', instanceId: failed.instance.id };
    }
    const last = chain[chain.length - 1];
    if (last?.instance?.status === 'success') {
        return { status: 'success', instanceId: last.instance.id };
    }
    return { status: 'inProgress', instanceId: null };
};
exports.resolveLinearChainState = resolveLinearChainState;
/**
 *
 * returns an interface to work with instances.
 */
const createInstanceUtils = ({ graphUtils, artifactInstances, pipelineRunRecord, }) => {
    const getInstanceById = (artifactInstanceId, caller) => {
        const artifactInstance = artifactInstances.find((instance) => instance.id === artifactInstanceId);
        (0, common_1.finleyAssert)(artifactInstance, `Missing artifact instance [id: ${artifactInstanceId}]${caller ? ` for ${caller}` : ''}`);
        return artifactInstance;
    };
    // This run's instance of an artifact, or null if it has none.
    const findInstanceByArtifactId = (artifactId) => artifactInstances.find((instance) => instance.artifactId === artifactId) ??
        null;
    // The scoped artifact behind a source instance; it must be of the given type.
    const getScopedSourceArtifact = (artifactId, artifactType, hasArtifactType) => {
        const artifact = graphUtils.getScopedArtifact({ artifactId });
        (0, common_1.finleyAssert)(artifact && hasArtifactType(artifact), `Expected type "${artifactType}" got [${artifact?.type}]`);
        return artifact;
    };
    // The instance of the first scoped artifact of the given type that matches.
    const findScopedInstance = (artifactType, hasArtifactType, predicate) => {
        const artifact = graphUtils
            .getScopedArtifactsOfType({ artifactType })
            .filter(hasArtifactType)
            .find(predicate);
        return artifact ? findInstanceByArtifactId(artifact.id) : null;
    };
    /**
     *
     * gets the status of the artifact instance
     */
    const getStatus = ({ artifactInstanceId, }) => {
        const artifactInstance = getInstanceById(artifactInstanceId, null);
        return artifactInstance.status;
    };
    /**
     *
     * returns an internal repayment tape instance for a raw repayment tape instance id, if one exists
     */
    const getInternalRepaymentTapeInstance = ({ rawRepaymentTapeInstanceId, }) => {
        const artifactInstance = getInstanceById(rawRepaymentTapeInstanceId, 'getInternalRepaymentTape');
        // if raw instance did not succeed, there is not point in looking for a nodes further down in the graph
        if (artifactInstance.status !== 'success') {
            return null;
        }
        const rawRepaymentTapeArtifact = getScopedSourceArtifact(artifactInstance.artifactId, 'rawRepaymentTape', dbNodes.rawRepaymentTape.hasArtifactType);
        return findScopedInstance('internalRepaymentTape', dbNodes.internalRepaymentTape.hasArtifactType, (artifact) => artifact.loanProductSlug === rawRepaymentTapeArtifact.loanProductSlug);
    };
    /**
     *
     * returns a cash response tape instance for a cash balance feed context instance id, if one exists
     */
    const getCashResponseTapeInstance = ({ cashBalanceFeedContextInstanceId, }) => {
        const artifactInstance = getInstanceById(cashBalanceFeedContextInstanceId, 'getCashResponseTapeInstance');
        // if raw instance did not succeed, there is not point in looking for a nodes further down in the graph
        if (artifactInstance.status !== 'success') {
            return null;
        }
        const cashBalanceFeedContextArtifact = getScopedSourceArtifact(artifactInstance.artifactId, 'cashBalancesFeedContext', dbNodes.cashBalancesFeedContext.hasArtifactType);
        return findScopedInstance('cashResponseTape', dbNodes.cashResponseTape.hasArtifactType, (artifact) => artifact.configuration.cashBalancesFeedContextArtifactSlug ===
            cashBalanceFeedContextArtifact.artifactSlug);
    };
    /**
     *
     * returns an org provided input instance for a given org provided key if it exists
     */
    const getOrgProvidedInputInstance = ({ orgProvidedKey, }) => {
        return findScopedInstance('orgProvidedInput', dbNodes.orgProvidedInput.hasArtifactType, (artifact) => artifact.configuration.orgProvidedKey === orgProvidedKey);
    };
    /**
     *
     * returns an org provided input instance for a given org provided key if it exists
     */
    const getGeneratedFinancialDataInstance = ({ financialDataArtifactSlug, }) => {
        return findScopedInstance('generatedFinancialData', dbNodes.generatedFinancialData.hasArtifactType, (artifact) => artifact.artifactSlug === financialDataArtifactSlug);
    };
    /**
     *
     * returns a list of assigned loan tapes instances for a raw loan tape artifact instance id
     */
    const getAssignedLoanTapeInstances = ({ rawLoanTapeArtifactInstanceId, }) => {
        const artifactInstance = getInstanceById(rawLoanTapeArtifactInstanceId, 'getAssignedLoanTapeInstances');
        // if raw instance did not succeed, there is not point in looking for a nodes further down in the graph
        if (artifactInstance.status !== 'success') {
            return [];
        }
        const rawLoanTapeArtifact = getScopedSourceArtifact(artifactInstance.artifactId, 'rawLoanTape', dbNodes.rawLoanTape.hasArtifactType);
        const assignedLoanTapeArtifacts = graphUtils
            .getScopedArtifactsOfType({
            artifactType: 'assignedLoanTape',
        })
            .filter(dbNodes.assignedLoanTape.hasArtifactType)
            .filter((artifact) => artifact.loanProductSlug === rawLoanTapeArtifact.loanProductSlug);
        return assignedLoanTapeArtifacts
            .filter((artifact) => {
            // if the loan tape was preassigned, we will have a cfSlug
            if (rawLoanTapeArtifact.creditFacilitySlug) {
                return (artifact.creditFacilitySlug ===
                    rawLoanTapeArtifact.creditFacilitySlug);
            }
            // otherwise, if the loan tape was a master loan tape, we do not have a cfSlug
            // in this case, the caller should filter all assigned loan tapes for the cfSlug they expect
            return true;
        })
            .flatMap((artifact) => {
            const assignedLoanTapeArtifactInstance = findInstanceByArtifactId(artifact.id);
            (0, common_1.finleyAssert)(artifact.type === 'assignedLoanTape', `Invalid artifact type. Expected assignedLoanTape got ${artifact.type}`);
            (0, common_1.finleyAssert)(artifact.creditFacilitySlug, '`Missing credit facility slug on assigned loan tape artifact');
            return assignedLoanTapeArtifactInstance
                ? {
                    ...assignedLoanTapeArtifactInstance,
                    creditFacilitySlug: artifact.creditFacilitySlug,
                }
                : [];
        });
    };
    const _getMeshStatus = ({ meshDocument, meshArtifactInstances, }) => {
        const requiredArtifactIds = meshDocument.artifacts.map(({ id }) => id);
        // create a map of artifactId => status
        const availableArtifactInstanceStatusMap = new Map(meshArtifactInstances.map(({ artifactId, status }) => [
            artifactId,
            status,
        ]));
        if (Array.from(availableArtifactInstanceStatusMap.values()).some((status) => status === 'error')) {
            return {
                status: 'error',
            };
        }
        const _getArtifactStatus = (artifactId) => {
            const status = availableArtifactInstanceStatusMap.get(artifactId);
            (0, common_1.finleyAssert)(status, `_getStatus: Missing instance for artifactId: ${artifactId}`);
            return status;
        };
        const finalOutputs = requiredArtifactIds.filter((id) => graphUtils.getOutputArtifacts(id).length === 0);
        const hasAllOutputs = finalOutputs.every((id) => availableArtifactInstanceStatusMap.has(id) &&
            _getArtifactStatus(id) === 'success');
        // do we have all outputs?
        if (hasAllOutputs) {
            return {
                status: 'success',
            };
        }
        const _isWaitingForNode = (artifactId) => {
            // see if the node is already started
            if (availableArtifactInstanceStatusMap.has(artifactId)) {
                // node is already started
                return false;
            }
            // see if the nodes inputs are available
            const nodeInputs = graphUtils.getInputs(artifactId);
            const hasNodeInputs = nodeInputs.every((id) => availableArtifactInstanceStatusMap.has(id) &&
                _getArtifactStatus(id) === 'success');
            if (!hasNodeInputs) {
                // the node is waiting for inputs. That means pipeline is not waiting for this node.
                return false;
            }
            // now we could say that yes - the pipeline is waiting for this node.
            return true;
        };
        // Note: meshInputs can be subset of actual inputs to the graph.
        // since some inputs like raw_cash_balance may not be configured to be provided as input.
        const meshInputArtifactIds = meshDocument.meshInputs.map(({ artifactId }) => artifactId);
        const waitingForInputs = meshInputArtifactIds.some((artifactId) => !availableArtifactInstanceStatusMap.has(artifactId));
        // do we have all inputs?
        if (waitingForInputs) {
            return {
                status: 'waitingForInput',
            };
        }
        // check if we are waiting for verification or assignment
        // for this we need to check if we are blocked on any userInputNeeded nodes.
        // if we are blocked on any of these nodes, we are waiting for assignment or verification.
        const userInputNodes = requiredArtifactIds
            .map((artifactId) => graphUtils.getArtifactById(artifactId))
            .filter(dbNodes.userInputNeeded.hasArtifactType);
        const verificationUserInputsArtifactIds = userInputNodes
            .filter(({ configuration: { inputType } }) => inputType === 'verificationApproval')
            .map(({ id }) => id);
        if (verificationUserInputsArtifactIds.some(_isWaitingForNode)) {
            return {
                status: 'waitingForVerificationApproval',
            };
        }
        const assignmentUserInputsArtifactIds = userInputNodes
            .filter(({ configuration: { inputType } }) => inputType === 'assignement')
            .map(({ id }) => id);
        if (assignmentUserInputsArtifactIds.some(_isWaitingForNode)) {
            return {
                status: 'waitingForAssignment',
            };
        }
        return {
            status: 'inProgress',
        };
    };
    const _getMeshArtifactInstances = (meshDocument) => {
        const meshArtifactIds = new Set(meshDocument.artifacts.map(({ id }) => id));
        const meshArtifactInstances = artifactInstances.filter(({ artifactId }) => meshArtifactIds.has(artifactId));
        // create a map of artifactId => status
        const availableArtifactInstanceStatusMap = new Map(meshArtifactInstances.map(({ artifactId, status }) => [
            artifactId,
            status,
        ]));
        // at this point we are going to create some synthetic instances
        // for the artifacts that are not started but their dependents are started.
        // this makes it easier to process the graph going forward.
        const syntheticArtifactIdToInstanceMap = new Map();
        meshArtifactInstances.forEach((instance) => {
            // check the input nodes of the instance
            const inputs = graphUtils.getRecursiveInputs(instance.artifactId);
            inputs.forEach((input) => {
                if (availableArtifactInstanceStatusMap.has(input) ||
                    syntheticArtifactIdToInstanceMap.has(input)) {
                    return;
                }
                syntheticArtifactIdToInstanceMap.set(input, {
                    ...instance,
                    id: makeSyntheticId(input),
                    artifactId: input,
                });
            });
        });
        return meshArtifactInstances.concat(Array.from(syntheticArtifactIdToInstanceMap.values()));
    };
    const _getCompleteMeshRunRecord = ({ meshDocument, }) => {
        const meshArtifactInstances = _getMeshArtifactInstances(meshDocument);
        if (meshArtifactInstances.length === 0) {
            return null;
        }
        // updatedAt is the max of all instances
        const updatedAt = meshArtifactInstances.reduce((max, { updated_at }) => (updated_at > max ? updated_at : max), meshArtifactInstances[0].updated_at);
        const { meshArtifactId } = finley_types_1.DataPipeline.readMeshDocumentId(meshDocument.meshDocumentId);
        const meshRunId = finley_types_1.DataPipeline.generateMeshRunId({
            pipelineRunId: pipelineRunRecord.id,
            meshArtifactId,
        });
        const { status } = _getMeshStatus({
            meshDocument,
            meshArtifactInstances,
        });
        const meshRunRecord = {
            meshRunId,
            meshDocumentId: meshDocument.meshDocumentId,
            cfSlugs: meshDocument.cfSlugs,
            lpSlugs: meshDocument.lpSlugs,
            meshName: meshDocument.meshName,
            startedAt: pipelineRunRecord.created_at,
            updatedAt,
            reportingDate: pipelineRunRecord.reportingDate,
            fileVersion: pipelineRunRecord.fileVersion,
            status,
        };
        return {
            meshRunRecord,
            instances: meshArtifactInstances.map(dbInstanceToPipelineInstance),
            meshDocument,
        };
    };
    /**
     *
     * MeshRun is a subset of pipeline run.
     * Each pipeline run can have multiple meshRuns.
     */
    const pipelineRunToMeshRuns = () => {
        // Note: getMeshDocuments returns only meshDocuments that that caller has access to.
        const meshDocuments = graphUtils.getMeshDocuments();
        return meshDocuments
            .map((meshDocument) => _getCompleteMeshRunRecord({ meshDocument }))
            .filter(common_1.typeguards.isNotNullOrUndefined);
    };
    const getCompleteMeshRunRecordById = ({ meshRunId, }) => {
        const { meshArtifactId } = finley_types_1.DataPipeline.readMeshRunId(meshRunId);
        const meshDocuments = graphUtils.getMeshDocuments();
        const meshDocument = meshDocuments.find((doc) => {
            const { meshArtifactId: docMeshArtifactId, } = finley_types_1.DataPipeline.readMeshDocumentId(doc.meshDocumentId);
            return docMeshArtifactId === meshArtifactId;
        });
        (0, common_1.finleyAssert)(meshDocument, `getCompleteMeshRunRecordById: Missing meshDocument for [meshRunId: ${meshRunId}]`);
        return _getCompleteMeshRunRecord({ meshDocument });
    };
    const getInitiatorIds = ({ artifactId, }) => {
        // find all inputs to the artifact
        const inputArtifactIds = graphUtils.getRecursiveInputs(artifactId);
        // for each input find the initiator id if exists
        const initiatorUserIds = inputArtifactIds
            .map((inputArtifactId) => {
            return (findInstanceByArtifactId(inputArtifactId)?.initiatorUserId ?? null);
        })
            .filter(common_1.typeguards.isNotNullOrUndefined);
        // return unique initiator ids
        return Array.from(new Set(initiatorUserIds));
    };
    /**
     *
     * returns the linear chain downstream of a raw instance's node — the node itself, then each
     * sole child while that child has no other input — with each node's instance in this run.
     * The chain ends before any fan-out or join, so it is the processing that belongs to this
     * raw input alone.
     */
    const getLinearChainInstances = ({ rawArtifactInstanceId, }) => {
        const rawInstance = getInstanceById(rawArtifactInstanceId, 'getLinearChainInstances');
        const nodeAt = (artifactId) => ({
            artifactId,
            instance: findInstanceByArtifactId(artifactId),
        });
        const walk = (artifactId) => {
            const outputs = graphUtils.getOutputs(artifactId);
            const [next] = outputs;
            if (outputs.length !== 1 ||
                !next ||
                graphUtils.getInputs(next).length !== 1) {
                return [nodeAt(artifactId)];
            }
            return [nodeAt(artifactId), ...walk(next)];
        };
        return walk(rawInstance.artifactId);
    };
    /**
     *
     * returns an uploaded input's raw instance in this run, by artifact slug, with the state
     * of its linear chain — or null while the run has no instance of it. Keyed by slug, not
     * artifact id, so the same input can be followed across runs on different graph versions.
     */
    const getUploadedInputState = ({ artifactSlug, }) => {
        const artifacts = graphUtils
            .getAllArtifacts()
            .filter((artifact) => artifact.artifactSlug === artifactSlug);
        (0, common_1.finleyAssert)(artifacts.length <= 1, `getUploadedInputState: expected at most one [artifactSlug:${artifactSlug}], found ${artifacts.length}`);
        const rawInstance = artifacts[0]
            ? findInstanceByArtifactId(artifacts[0].id)
            : null;
        if (!rawInstance) {
            return null;
        }
        return {
            rawInstance,
            state: (0, exports.resolveLinearChainState)(getLinearChainInstances({ rawArtifactInstanceId: rawInstance.id })),
        };
    };
    return {
        graphUtils,
        // type specific instances
        getLinearChainInstances,
        getUploadedInputState,
        getInternalRepaymentTapeInstance,
        getCashResponseTapeInstance,
        getOrgProvidedInputInstance,
        getAssignedLoanTapeInstances,
        getGeneratedFinancialDataInstance,
        // instance utility.
        getInitiatorIds,
        // mesh
        getStatus,
        pipelineRunToMeshRuns,
        getCompleteMeshRunRecordById,
    };
};
exports.createInstanceUtils = createInstanceUtils;
//# sourceMappingURL=dbInstanceUtils.js.map